import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';
import config from 'config';
import Logger from 'utils/logger';
import i18n from 'app/utils/i18n';
import { showToast } from 'app/hooks/useToast';
import {
  CognitoTokenManager,
  SelectedAccount,
} from '../utils/cognitoTokenManager';
import {
  CognitoApi,
  CognitoAccount,
  ValidateTokenResponse,
  FederatedLoginResponse,
  RefreshTokenResponse,
} from '../api/cognitoApi';
import { TokenRefreshService } from '../utils/tokenRefreshService';

type MfaChallengeType = 'SMS_MFA' | 'SOFTWARE_TOKEN_MFA' | null;

type AuthFlowState =
  | 'idle'
  | 'authenticating'
  | 'needs_linking'
  | 'needs_account_selection'
  | 'authenticated';

interface CognitoAuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: CognitoUser | null;
  error: Error | null;
  accessToken: string | null;
  idToken: string | null;
  refreshToken: string | null;
  mfaRequired: boolean;
  mfaType: MfaChallengeType;
  flowState: AuthFlowState;
  availableAccounts: CognitoAccount[];
  selectedAccount: SelectedAccount | null;
  needsLinking: boolean;
  cognitoSub: string | null;
  email: string | null;
}

interface CognitoAuthActions {
  login: (
    email: string,
    password: string,
  ) => Promise<CognitoUserSession | 'MFA_REQUIRED'>;
  logout: () => Promise<void>;
  refreshSession: () => Promise<RefreshTokenResponse | null>;
  refreshTokensViaApi: () => Promise<RefreshTokenResponse | null>;
  getAccessToken: () => string | null;
  handleOAuthCallback: (code: string, redirectUri?: string) => Promise<void>;
  confirmMfaCode: (code: string) => Promise<CognitoUserSession>;
  cancelMfa: () => void;
  validateAndLink: () => Promise<ValidateTokenResponse>;
  linkAccounts: (accountIds: number[]) => Promise<void>;
  selectAccount: (account: CognitoAccount) => Promise<void>;
  handleFederatedLogin: (
    provider: 'Google' | 'Facebook' | 'Apple',
    providerUserId: string,
    email: string,
  ) => Promise<FederatedLoginResponse>;
  resetAuthFlow: () => void;
}

interface CognitoAuthContextValue
  extends CognitoAuthState,
    CognitoAuthActions {}

const CognitoAuthStateContext = createContext<CognitoAuthState | undefined>(
  undefined,
);

const CognitoAuthActionsContext = createContext<CognitoAuthActions | undefined>(
  undefined,
);

const userPool = new CognitoUserPool({
  UserPoolId: config.COGNITO.userPoolId,
  ClientId: config.COGNITO.clientId,
});

export const CognitoAuthProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [state, setState] = useState<CognitoAuthState>({
    isAuthenticated: false,
    isLoading: true,
    user: null,
    error: null,
    accessToken: null,
    idToken: null,
    refreshToken: null,
    mfaRequired: false,
    mfaType: null,
    flowState: 'idle',
    availableAccounts: [],
    selectedAccount: CognitoTokenManager.getSelectedAccount(),
    needsLinking: false,
    cognitoSub: null,
    email: null,
  });

  const stateRef = useRef(state);
  stateRef.current = state;

  const mfaResolverRef = useRef<{
    resolve: (session: CognitoUserSession) => void;
    reject: (error: Error) => void;
    cognitoUser: CognitoUser;
  } | null>(null);

  const updateSessionState = useCallback((session: CognitoUserSession) => {
    setState((prev) => ({
      ...prev,
      isAuthenticated: true,
      isLoading: false,
      accessToken: session.getAccessToken().getJwtToken(),
      idToken: session.getIdToken().getJwtToken(),
      refreshToken: session.getRefreshToken().getToken(),
      error: null,
    }));
  }, []);

  const checkCurrentSession = useCallback(() => {
    const tokens = CognitoTokenManager.getTokens();

    if (!tokens?.accessToken || !tokens?.refreshToken) {
      setState((prev) => ({
        ...prev,
        isLoading: false,
        isAuthenticated: false,
      }));
      return;
    }

    setState((prev) => ({
      ...prev,
      isLoading: false,
      isAuthenticated: true,
      accessToken: tokens.accessToken,
      idToken: tokens.idToken,
      refreshToken: tokens.refreshToken,
      error: null,
    }));
  }, []);

  useEffect(() => {
    checkCurrentSession();
  }, [checkCurrentSession]);

  const login = useCallback(
    (
      email: string,
      password: string,
    ): Promise<CognitoUserSession | 'MFA_REQUIRED'> => {
      return new Promise((resolve, reject) => {
        setState((prev) => ({ ...prev, isLoading: true, error: null }));

        const cognitoUser = new CognitoUser({
          Username: email,
          Pool: userPool,
        });

        const authDetails = new AuthenticationDetails({
          Username: email,
          Password: password,
        });

        cognitoUser.authenticateUser(authDetails, {
          onSuccess: (session) => {
            Logger.log('Cognito login successful');
            setState((prev) => ({ ...prev, user: cognitoUser }));
            updateSessionState(session);
            resolve(session);
          },
          onFailure: (err) => {
            Logger.error('Cognito login failed', err);
            setState((prev) => ({
              ...prev,
              isLoading: false,
              isAuthenticated: false,
              error: err,
            }));
            reject(err);
          },
          newPasswordRequired: (userAttributes) => {
            Logger.log('New password required', userAttributes);
            setState((prev) => ({
              ...prev,
              isLoading: false,
              error: new Error('NEW_PASSWORD_REQUIRED'),
            }));
            reject(new Error('NEW_PASSWORD_REQUIRED'));
          },
          totpRequired: () => {
            Logger.log('TOTP MFA required');
            mfaResolverRef.current = {
              resolve: resolve as (session: CognitoUserSession) => void,
              reject,
              cognitoUser,
            };
            setState((prev) => ({
              ...prev,
              isLoading: false,
              user: cognitoUser,
              mfaRequired: true,
              mfaType: 'SOFTWARE_TOKEN_MFA',
            }));
            resolve('MFA_REQUIRED');
          },
          mfaRequired: () => {
            Logger.log('SMS MFA required');
            mfaResolverRef.current = {
              resolve: resolve as (session: CognitoUserSession) => void,
              reject,
              cognitoUser,
            };
            setState((prev) => ({
              ...prev,
              isLoading: false,
              user: cognitoUser,
              mfaRequired: true,
              mfaType: 'SMS_MFA',
            }));
            resolve('MFA_REQUIRED');
          },
        });
      });
    },
    [updateSessionState],
  );

  const logout = useCallback((): Promise<void> => {
    return new Promise((resolve) => {
      const cognitoUser = userPool.getCurrentUser();

      if (cognitoUser) {
        cognitoUser.signOut();
      }

      CognitoTokenManager.signOut();

      setState({
        isAuthenticated: false,
        isLoading: false,
        user: null,
        error: null,
        accessToken: null,
        idToken: null,
        refreshToken: null,
        mfaRequired: false,
        mfaType: null,
        flowState: 'idle',
        availableAccounts: [],
        selectedAccount: null,
        needsLinking: false,
        cognitoSub: null,
        email: null,
      });

      resolve();
    });
  }, []);

  const refreshSession =
    useCallback(async (): Promise<RefreshTokenResponse | null> => {
      try {
        const response = await TokenRefreshService.refresh();

        setState((prev) => ({
          ...prev,
          accessToken: response.access_token,
          idToken: response.id_token,
          refreshToken: response.refresh_token || prev.refreshToken,
          isAuthenticated: true,
          error: null,
        }));

        return response;
      } catch (err) {
        Logger.error(
          'Failed to refresh session via API',
          (err as Error).message,
        );
        throw err;
      }
    }, []);

  const refreshTokensViaApi =
    useCallback(async (): Promise<RefreshTokenResponse | null> => {
      try {
        const response = await TokenRefreshService.refresh();

        setState((prev) => ({
          ...prev,
          accessToken: response.access_token,
          idToken: response.id_token,
          refreshToken: response.refresh_token,
          isAuthenticated: true,
          error: null,
        }));

        return response;
      } catch (err) {
        const error = err as Error;
        Logger.error('Failed to refresh tokens via API', error.message);
        throw error;
      }
    }, []);

  const getAccessToken = useCallback((): string | null => {
    return stateRef.current.accessToken;
  }, []);

  const handleOAuthCallback = useCallback(
    async (_code: string, _redirectUri?: string): Promise<void> => {
      throw new Error(i18n.t('auth.oauthCallbackNotSupported'));
    },
    [],
  );

  const confirmMfaCode = useCallback(
    (code: string): Promise<CognitoUserSession> => {
      return new Promise((resolve, reject) => {
        if (!mfaResolverRef.current) {
          reject(new Error(i18n.t('auth.noMfaSession')));
          return;
        }

        setState((prev) => ({ ...prev, isLoading: true, error: null }));

        const { cognitoUser } = mfaResolverRef.current;

        if (stateRef.current.mfaType === 'SOFTWARE_TOKEN_MFA') {
          cognitoUser.sendMFACode(
            code,
            {
              onSuccess: (session) => {
                Logger.log('TOTP MFA verification successful');
                mfaResolverRef.current = null;
                setState((prev) => ({
                  ...prev,
                  user: cognitoUser,
                  mfaRequired: false,
                  mfaType: null,
                }));
                updateSessionState(session);
                resolve(session);
              },
              onFailure: (err) => {
                Logger.error('TOTP MFA verification failed', err);
                setState((prev) => ({
                  ...prev,
                  isLoading: false,
                  error: err,
                }));
                reject(err);
              },
            },
            'SOFTWARE_TOKEN_MFA',
          );
        } else {
          cognitoUser.sendMFACode(
            code,
            {
              onSuccess: (session) => {
                Logger.log('SMS MFA verification successful');
                mfaResolverRef.current = null;
                setState((prev) => ({
                  ...prev,
                  user: cognitoUser,
                  mfaRequired: false,
                  mfaType: null,
                }));
                updateSessionState(session);
                resolve(session);
              },
              onFailure: (err) => {
                Logger.error('SMS MFA verification failed', err);
                setState((prev) => ({
                  ...prev,
                  isLoading: false,
                  error: err,
                }));
                reject(err);
              },
            },
            'SMS_MFA',
          );
        }
      });
    },
    [updateSessionState],
  );

  const cancelMfa = useCallback(() => {
    mfaResolverRef.current = null;
    setState((prev) => ({
      ...prev,
      isLoading: false,
      mfaRequired: false,
      mfaType: null,
      user: null,
      error: null,
    }));
  }, []);

  const validateAndLink =
    useCallback(async (): Promise<ValidateTokenResponse> => {
      const tokens = CognitoTokenManager.getTokens();
      const idToken = stateRef.current.idToken || tokens?.idToken;
      const accessToken = stateRef.current.accessToken || tokens?.accessToken;

      if (!idToken) {
        throw new Error(i18n.t('auth.noIdToken'));
      }

      setState((prev) => ({ ...prev, isLoading: true, error: null }));

      try {
        const response = await CognitoApi.validateToken({
          id_token: idToken,
          access_token: accessToken || undefined,
        });
        Logger.log('Token validation response:', response);

        if (response.accounts.length === 0) {
          setState((prev) => ({
            ...prev,
            isLoading: false,
            flowState: 'idle',
            cognitoSub: response.cognito_sub,
            email: response.email,
            error: new Error(i18n.t('auth.noAccountsFound')),
          }));
          return response;
        }

        if (response.needs_linking) {
          setState((prev) => ({
            ...prev,
            isLoading: false,
            flowState:
              response.accounts.length > 1
                ? 'needs_account_selection'
                : 'needs_linking',
            availableAccounts: response.accounts,
            needsLinking: true,
            cognitoSub: response.cognito_sub,
            email: response.email,
          }));
        } else if (response.accounts.length > 1) {
          setState((prev) => ({
            ...prev,
            isLoading: false,
            flowState: 'needs_account_selection',
            availableAccounts: response.accounts,
            needsLinking: false,
            cognitoSub: response.cognito_sub,
            email: response.email,
          }));
        } else {
          const account = response.accounts[0];
          const selectedAccount: SelectedAccount = {
            accountId: account.account_id,
            accountName: account.account_name,
            userRole: account.user_role,
            userName: account.user_name,
          };
          CognitoTokenManager.setSelectedAccount(selectedAccount);
          setState((prev) => ({
            ...prev,
            isLoading: false,
            isAuthenticated: true,
            flowState: 'authenticated',
            availableAccounts: response.accounts,
            selectedAccount,
            needsLinking: false,
            cognitoSub: response.cognito_sub,
            email: response.email,
          }));
        }

        return response;
      } catch (err) {
        const error = err as Error;
        Logger.error('Token validation failed', error.message);
        showToast(
          (err as { userMessage?: string })?.userMessage ||
            i18n.t('somethingWentWrong'),
          'error',
        );
        setState((prev) => ({
          ...prev,
          isLoading: false,
          error,
        }));
        throw error;
      }
    }, []);

  const linkAccounts = useCallback(
    async (accountIds: number[]): Promise<void> => {
      const idToken =
        stateRef.current.idToken || CognitoTokenManager.getIdToken();
      if (!idToken) {
        throw new Error(i18n.t('auth.noIdToken'));
      }

      setState((prev) => ({ ...prev, isLoading: true, error: null }));

      try {
        await CognitoApi.linkAccounts({
          id_token: idToken,
          account_ids: accountIds,
        });
        Logger.log('Accounts linked successfully');

        await validateAndLink();
      } catch (err) {
        const error = err as Error;
        Logger.error('Account linking failed', error.message);
        showToast(
          (err as { userMessage?: string })?.userMessage ||
            i18n.t('somethingWentWrong'),
          'error',
        );
        setState((prev) => ({
          ...prev,
          isLoading: false,
          error,
        }));
        throw error;
      }
    },
    [validateAndLink],
  );

  const selectAccount = useCallback(
    async (account: CognitoAccount): Promise<void> => {
      const idToken =
        stateRef.current.idToken || CognitoTokenManager.getIdToken();
      if (!idToken) {
        throw new Error(i18n.t('auth.noIdToken'));
      }

      setState((prev) => ({ ...prev, isLoading: true, error: null }));

      try {
        if (stateRef.current.needsLinking) {
          await CognitoApi.linkAccounts({
            id_token: idToken,
            account_ids: [account.account_id],
          });
        }

        await CognitoApi.selectAccount({
          id_token: idToken,
          account_id: account.account_id,
        });

        const selectedAccount: SelectedAccount = {
          accountId: account.account_id,
          accountName: account.account_name,
          userRole: account.user_role,
          userName: account.user_name,
        };
        CognitoTokenManager.setSelectedAccount(selectedAccount);

        setState((prev) => ({
          ...prev,
          isLoading: false,
          isAuthenticated: true,
          flowState: 'authenticated',
          selectedAccount,
          needsLinking: false,
        }));
      } catch (err) {
        const error = err as Error;
        Logger.error('Account selection failed', error.message);
        showToast(
          (err as { userMessage?: string })?.userMessage ||
            i18n.t('somethingWentWrong'),
          'error',
        );
        setState((prev) => ({
          ...prev,
          isLoading: false,
          error,
        }));
        throw error;
      }
    },
    [],
  );

  const handleFederatedLogin = useCallback(
    async (
      provider: 'Google' | 'Facebook' | 'Apple',
      providerUserId: string,
      email: string,
    ): Promise<FederatedLoginResponse> => {
      const tokens = CognitoTokenManager.getTokens();
      if (!tokens) {
        throw new Error(i18n.t('auth.noTokensAvailable'));
      }

      setState((prev) => ({ ...prev, isLoading: true, error: null }));

      try {
        const response = await CognitoApi.federatedLogin({
          provider,
          provider_user_id: providerUserId,
          email,
          cognito_access_token: tokens.accessToken,
          cognito_id_token: tokens.idToken,
        });
        Logger.log('Federated login response:', response);

        if (response.accounts.length === 0) {
          setState((prev) => ({
            ...prev,
            isLoading: false,
            flowState: 'idle',
            cognitoSub: response.cognito_sub,
            email: response.email,
            error: new Error(i18n.t('auth.noAccountsFound')),
          }));
          return response;
        }

        if (response.needs_account_selection) {
          setState((prev) => ({
            ...prev,
            isLoading: false,
            flowState: 'needs_account_selection',
            availableAccounts: response.accounts,
            cognitoSub: response.cognito_sub,
            email: response.email,
          }));
        } else {
          const account = response.accounts[0];
          const selectedAccount: SelectedAccount = {
            accountId: account.account_id,
            accountName: account.account_name,
            userRole: account.user_role,
            userName: account.user_name,
          };
          CognitoTokenManager.setSelectedAccount(selectedAccount);
          setState((prev) => ({
            ...prev,
            isLoading: false,
            isAuthenticated: true,
            flowState: 'authenticated',
            availableAccounts: response.accounts,
            selectedAccount,
            cognitoSub: response.cognito_sub,
            email: response.email,
          }));
        }

        return response;
      } catch (err) {
        const error = err as Error;
        Logger.error('Federated login failed', error.message);
        setState((prev) => ({
          ...prev,
          isLoading: false,
          error,
        }));
        throw error;
      }
    },
    [],
  );

  const resetAuthFlow = useCallback(() => {
    CognitoTokenManager.signOut();
    setState({
      isAuthenticated: false,
      isLoading: false,
      user: null,
      error: null,
      accessToken: null,
      idToken: null,
      refreshToken: null,
      mfaRequired: false,
      mfaType: null,
      flowState: 'idle',
      availableAccounts: [],
      selectedAccount: null,
      needsLinking: false,
      cognitoSub: null,
      email: null,
    });
  }, []);

  const actions = useMemo<CognitoAuthActions>(
    () => ({
      login,
      logout,
      refreshSession,
      refreshTokensViaApi,
      getAccessToken,
      handleOAuthCallback,
      confirmMfaCode,
      cancelMfa,
      validateAndLink,
      linkAccounts,
      selectAccount,
      handleFederatedLogin,
      resetAuthFlow,
    }),
    [
      login,
      logout,
      refreshSession,
      refreshTokensViaApi,
      getAccessToken,
      handleOAuthCallback,
      confirmMfaCode,
      cancelMfa,
      validateAndLink,
      linkAccounts,
      selectAccount,
      handleFederatedLogin,
      resetAuthFlow,
    ],
  );

  return (
    <CognitoAuthStateContext.Provider value={state}>
      <CognitoAuthActionsContext.Provider value={actions}>
        {children}
      </CognitoAuthActionsContext.Provider>
    </CognitoAuthStateContext.Provider>
  );
};

export const useCognitoAuthState = (): CognitoAuthState => {
  const context = useContext(CognitoAuthStateContext);

  if (!context) {
    throw new Error(
      'useCognitoAuthState must be used within a CognitoAuthProvider',
    );
  }

  return context;
};

export const useCognitoAuthActions = (): CognitoAuthActions => {
  const context = useContext(CognitoAuthActionsContext);

  if (!context) {
    throw new Error(
      'useCognitoAuthActions must be used within a CognitoAuthProvider',
    );
  }

  return context;
};

export const useCognitoAuth = (): CognitoAuthContextValue => {
  const state = useCognitoAuthState();
  const actions = useCognitoAuthActions();

  return useMemo(() => ({ ...state, ...actions }), [state, actions]);
};
